import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { BylineConnectionsTrigger, BylineConnectionsWrapper } from './BylineConnectionsTrigger.tsx';
import { MAX_BYLINE_CONNECTION_ICONS } from './utils/getBylineConnectionIcons.ts';
import { createPlaceholderConnectionIcon } from './utils/placeholderConnectionIcon.tsx';

const i18n = defineMessages({
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
});

type BylineConnectionsPlaceholderVisualProps = {
	testId?: string;
	// Number of disabled app tiles to render; defaults to the maximum when the count is unknown.
	// Pass 0 for the logo-and-label mode used by the inbound-only placeholder.
	iconCount?: number;
};

/**
 * The disabled Teamwork Graph logo + label + app-icon stack placeholder shown while the byline
 * connections item is resolving. Shared by the SSR/loadable placeholder and the
 * loaded component's own loading state so the footprint lines up across the
 * SSR → client-load → data-load → item handoff, avoiding layout shift. It renders
 * `iconCount` disabled tiles — sized from the outbound domain estimate — so the
 * stack matches the number of icons the loaded byline is expected to show. An
 * `iconCount` of 0 renders the logo and label only, matching the inbound-only
 * trigger, which shows no app-icon stack.
 */
export const BylineConnectionsPlaceholderVisual = ({
	testId = 'byline-connections-ssr-placeholder',
	iconCount = MAX_BYLINE_CONNECTION_ICONS,
}: BylineConnectionsPlaceholderVisualProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const placeholderIcons = useMemo(
		() =>
			Array.from(
				{ length: Math.min(Math.max(iconCount, 0), MAX_BYLINE_CONNECTION_ICONS) },
				(_, index) => createPlaceholderConnectionIcon(index),
			),
		[iconCount],
	);

	return (
		<BylineConnectionsWrapper
			appearance="placeholder"
			role="status"
			aria-label={formatMessage(i18n.loadingConnections)}
			data-testid={testId}
		>
			<BylineConnectionsTrigger
				appearance="placeholder"
				links={[]}
				additionalIcons={placeholderIcons}
			/>
		</BylineConnectionsWrapper>
	);
};
